import React, { useState, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Camera, MessageSquare, X, Upload, Loader2, CheckCircle, AlertCircle, Eye, Trash2, Image as ImageIcon } from 'lucide-react';
import cloudinaryService from '../cloudinaryService';
import { useTranslation } from '../hooks/useTranslation';
import '../pwa-styles.css';
// DQGMS · P-001 (mockup) — tri-state response control renders below the row
// when REACT_APP_USE_MOCK is on. In production builds the flag is unset
// and the block that uses it becomes dead code (tree-shaken).
import TriStateResponseControl from './TriStateResponseControl';
import KoApprovalModal from './KoApprovalModal';
import * as qgsStore from '../qgsSubmissionStore';
import * as qgsApi from '../qgsApi';

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}


// Image Preview Component
const ImagePreview = ({ image, onRemove, onView, t }) => (
  <div className="relative group">
    <img
      src={cloudinaryService.getThumbnailUrl(image.url, 150)}
      alt={image.alt}
      className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
      onClick={() => onView(image)}
    />
    <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
      <button
        onClick={() => onView(image)}
        className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
        title={t('checklist.view_full_size')}
      >
        <Eye className="h-4 w-4 text-gray-700" />
      </button>
      <button
        onClick={() => onRemove(image.id)}
        className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
        title={t('checklist.remove_image')}
      >
        <Trash2 className="h-4 w-4 text-white" />
      </button>
    </div>
    {image.uploadStatus === 'uploading' && (
      <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    )}
    {image.uploadStatus === 'error' && (
      <div className="absolute top-2 right-2">
        <AlertCircle className="h-5 w-5 text-red-500" />
      </div>
    )}
    {image.uploadStatus === 'success' && (
      <div className="absolute top-2 right-2">
        <CheckCircle className="h-5 w-5 text-green-500" />
      </div>
    )}
  </div>
);

// ChecklistPage Component
function ChecklistPage({
  className,
  onNext,
  onPrevious,
  onNavigate,
  checklistItems = [],
  onItemChange,
  categoryTitle = "DOCUMENTAZIONE",
  categoryTranslation = "Documentation",
  controlItemTitle = "Document collection pouch",
  controlItemTitleItalian = "Bustina raccolta documenti",
  currentItemIndex = 1,
  totalItems = 1,
  selectedForm,
  onOpenFormDetails,
  isNavigationDisabled = false, // Add this prop
  isChecklistComplete = false, // Add completion status prop
  // Progress props
  currentCategoryIndex = 1,
  totalCategories = 1,
  currentControlItemIndex = 1,
  totalControlItems = 1,
  // Score props
  currentScore = null,
  showScore = false,
  showSummaryButton = false, // NEW: Show Summary button only when audit is complete
  // QGS v2 · injected by ChecklistPageLoop. Resolves to a submission id
  // (creates the envelope lazily on first call). Needed by the KO
  // approval modal so a KO can be committed straight away.
  onEnsureQgsSubmission = null,
}) {
  const { t, language } = useTranslation();
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tempComment, setTempComment] = useState('');
  const [uploadingItems, setUploadingItems] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [viewImageDialog, setViewImageDialog] = useState({ open: false, image: null });
  const [cloudinaryStatus, setCloudinaryStatus] = useState(cloudinaryService.isConfigured());
  const [imageOptionsDialog, setImageOptionsDialog] = useState({ open: false, itemId: null });
  const [cameraDialog, setCameraDialog] = useState({ open: false, itemId: null });
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = React.useRef(null);
  const uploadInProgressRef = useRef({}); // Track which items have uploads in progress

  // DQGMS — per-item tri-state response + lookups. Local state is a mirror
  // of qgsSubmissionStore so navigation between control items no longer
  // drops earlier selections; the store is the authoritative in-memory
  // record for the whole audit session, and the backend counts land back
  // through submission_summary on every KO / Rework POST.
  // Gated on BOTH the env flag AND the operator's app-mode choice OR the
  // loaded form's gate_type — QG1/QG_FAN/QG2/B_KANSA are QGS gates.
  let __appMode = null;
  try { __appMode = localStorage.getItem('dqgms_app_mode'); } catch (e) { /* ignore */ }
  const __qgsGates = new Set(['QG1', 'QG_FAN', 'QG2', 'B_KANSA']);
  const __isQgsForm = !!(selectedForm && selectedForm.gate_type && __qgsGates.has(selectedForm.gate_type));
  // Enabled by any of: prod build with REACT_APP_APP_MODE=QGS, or the
  // mockup dev combo (REACT_APP_USE_MOCK + AppSelector pick), or the
  // loaded form itself being on a QGS gate.
  const mockOn = process.env.REACT_APP_APP_MODE === 'QGS'
    || __isQgsForm
    || (process.env.REACT_APP_USE_MOCK === 'true' && __appMode === 'QGS');

  const [triResponses, setTriResponses] = useState({}); // { [itemId]: 'OK'|'REWORK'|'KO' }
  const [triDetails, setTriDetails] = useState({});     // { [itemId]: { defect_type_id, ... } }
  const [backendSummary, setBackendSummary] = useState(qgsStore.getSubmissionSummary());
  // QGS v2 · KO approval modal state — { open, item, saving? }
  const [koApproval, setKoApproval] = useState({ open: false, item: null });

  // Subscribe once so the header pills follow the backend's authoritative
  // knockouts_count / reworks_count after every write.
  React.useEffect(() => {
    return qgsStore.subscribe(() => setBackendSummary(qgsStore.getSubmissionSummary()));
  }, []);

  // Prime local state from the store whenever the operator lands on a new
  // control item (checklistItems changes). Without this, going Back / Forward
  // between control items would show empty tri-states even though the store
  // has the prior selections.
  React.useEffect(() => {
    if (!mockOn || checklistItems.length === 0) return;
    const nextResponses = {};
    const nextDetails = {};
    for (const it of checklistItems) {
      const rec = qgsStore.getRecord(it.id);
      if (rec) {
        if (rec.response) nextResponses[it.id] = rec.response;
        if (rec.details) nextDetails[it.id] = rec.details;
      }
    }
    setTriResponses(prev => ({ ...prev, ...nextResponses }));
    setTriDetails(prev => ({ ...prev, ...nextDetails }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistItems, mockOn]);
  const [triLookups, setTriLookups] = useState({ defect_types: [], defect_locations: [], rework_types: [] });
  React.useEffect(() => {
    if (!mockOn) return;
    let cancelled = false;
    (async () => {
      // QGS-real lookups from the Django backend on :8000. Absolute URL
      // keeps MSW out of the way. Response is normalised to the
      // {id, code, label_en} shape TriStateResponseControl consumes.
      const API = (process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000') + '/api/frontend/qgs';
      const parse = async (path) => {
        try {
          const r = await fetch(`${API}${path}?page_size=500`, { credentials: 'include' });
          if (!r.ok) return [];
          const b = await r.json();
          const rows = b.results || b || [];
          return rows.map(x => ({
            id: x.id,
            code: x.code,
            label_en: x.label_en,
            label_it: x.label_it || '',
          }));
        } catch { return []; }
      };
      const [dt, dl, rt] = await Promise.all([
        parse('/defect-types/'),
        parse('/defect-locations/'),
        parse('/rework-types/'),
      ]);
      if (!cancelled) {
        setTriLookups({ defect_types: dt, defect_locations: dl, rework_types: rt });
      }
    })();
    return () => { cancelled = true; };
  }, [mockOn]);

  // Scope the global master lookups down to what the individual Check
  // allows via its ko_allowed_* / rework_allowed_* M2Ms. Empty M2M
  // means "no restriction" — fall back to the full master.
  const scopedLookupsFor = React.useCallback((item) => {
    const asSet = (arr) => new Set((arr || []).map(String));
    const scope = (masters, allowed) => {
      if (!allowed || allowed.length === 0) return masters;
      const s = asSet(allowed);
      return masters.filter(o => s.has(String(o.id)));
    };
    return {
      defect_types: scope(triLookups.defect_types, item.ko_allowed_defect_types),
      defect_locations: scope(triLookups.defect_locations, item.ko_allowed_defect_locations),
      rework_types: scope(triLookups.rework_types, item.rework_allowed_types),
    };
  }, [triLookups]);

  // DQGMS mockup — clicking REWORK/KO on a defect row (or OK on NPD) triggers:
  //   1. Auto-tick of the row's own leftmost checkbox (marks it handled)
  //   2. Mutex per control item: NPD ↔ defect responses are mutually exclusive
  //      · Selecting NPD → wipes REWORK/KO on siblings AND unticks their checkboxes
  //      · Selecting a defect REWORK/KO → wipes NPD's OK AND unticks NPD's checkbox
  const handleTriChange = (item, next) => {
    const isNpd = (item.deductionPoints || 0) === 0;
    const npdItem = checklistItems.find(i => (i.deductionPoints || 0) === 0);
    const defectItems = checklistItems.filter(i => (i.deductionPoints || 0) !== 0);

    // Update the tri-state map with cascade
    setTriResponses(prev => {
      const nextMap = { ...prev, [item.id]: next };
      if (isNpd && next) {
        defectItems.forEach(d => { nextMap[d.id] = null; });
      } else if (!isNpd && next && npdItem) {
        nextMap[npdItem.id] = null;
      }
      return nextMap;
    });

    // Mirror to the store so the pick survives navigation between control
    // items and stays available at flush / complete time. The store also
    // holds the row's contextual data (category, control item, photo URL)
    // that the backend POST needs.
    const controlItemId = item.controlItemData?.control_item_id
      || item.controlItemId
      || item.originalCheck?.control_item_id;
    const categoryId = item.categoryId
      || item.originalCheck?.category_id;
    const photoUrl = (item.images && item.images.length > 0)
      ? (item.images[0].url || '')
      : '';
    if (next) {
      qgsStore.setRecord(item.id, {
        response: next,
        categoryId,
        controlItemId,
        checkLabel: item.label || item.labelItalian || '',
        comment: item.comment || '',
        photoUrl,
        // Preserve any existing details so a later tri-state pick doesn't
        // wipe defect_type_id etc. that the operator already filled.
      });
      // Cascade side-effects: siblings wiped by the mutex must also clear
      // in the store, otherwise a stale NPD or defect pick would still be
      // flushed to the backend later.
      if (isNpd) {
        defectItems.forEach(d => qgsStore.setRecord(d.id, { response: null, synced: false }));
      } else if (npdItem) {
        qgsStore.setRecord(npdItem.id, { response: null, synced: false });
      }
    } else {
      qgsStore.setRecord(item.id, { response: null, synced: false });
    }

    // Own row's checkbox tracks the tri-state (tick on select, untick on clear)
    handleItemChange({ ...item, isChecked: !!next });

    // Sibling checkboxes: clear when their tri-state is being wiped by the mutex
    if (isNpd && next) {
      defectItems.forEach(d => {
        if (d.isChecked) handleItemChange({ ...d, isChecked: false });
      });
    } else if (!isNpd && next && npdItem && npdItem.isChecked) {
      handleItemChange({ ...npdItem, isChecked: false });
    }
  };

  const handleTriDetailsChange = (itemId, patch) => {
    setTriDetails(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), ...patch } }));
    // Mirror partial details onto the store record (which is created
    // lazily on the first setRecord — safe even if handleTriChange hasn't
    // fired yet, because the operator can start filling defect_type first).
    qgsStore.setRecord(itemId, {
      details: { ...(qgsStore.getRecord(itemId)?.details || {}), ...patch },
      synced: false,
    });
  };

  // QGS v2 · KO row is "ready for approval" once tri-state is KO AND the
  // two mandatory pickers on the KO panel are filled. Called from the
  // render to decide whether the "Submit for approval" button appears.
  const isKoReadyForApproval = (item) => {
    const rec = qgsStore.getRecord(item.id);
    if (!rec || rec.response !== 'KO' || rec.synced) return false;
    const d = rec.details || {};
    return !!(d.defect_type_id && d.defect_location_id);
  };

  // Called by the KoApprovalModal when the operator hits Approve. We
  // resolve the submission id lazily (ChecklistPageLoop owns that logic),
  // then POST the KO to the create-with-approval endpoint. On success we
  // mark the row synced so the batched flush on Next skips it.
  const submitKoApproval = async ({ username, pin }) => {
    if (!koApproval.item) throw new Error('No KO row in-flight');
    const item = koApproval.item;
    const rec = qgsStore.getRecord(item.id) || {};
    const d = rec.details || {};

    if (!onEnsureQgsSubmission) {
      const err = new Error('QGS submission bridge not wired');
      err.status = 500;
      throw err;
    }
    const submissionId = await onEnsureQgsSubmission();

    const photoUrl = (item.images && item.images.length > 0)
      ? (item.images[0].url || '')
      : (rec.photoUrl || '');

    const saved = await qgsApi.createKnockoutWithApproval({
      submission: submissionId,
      check_field: Number(item.id),
      defect_type: d.defect_type_id,
      defect_location: d.defect_location_id,
      initial_comment: (item.comment || d.note || ''),
      initial_photo: photoUrl,
      approver_username: username,
      pin,
    });

    // Backend committed. Mirror status onto the store so the batched
    // flush on Next skips this row.
    qgsStore.setRecord(item.id, {
      synced: true,
      approver: saved.approver || null,
      backendKoId: saved.id,
      backendKoStatus: saved.status,
    });
    if (saved.submission_summary) qgsStore.setSubmissionSummary(saved.submission_summary);
    return saved;
  };

  // QGS v2 · Next only lights up when every defect row in the current
  // control item carries a valid response. OK is enough on its own.
  // KO and REWORK both need image + comment, and KO additionally needs
  // the LL signature. The NPD row is hidden in QGS mode (see render
  // below) so it doesn't count here.
  const mockNextEnabled = mockOn && (() => {
    const defectItems = checklistItems.filter(i => (i.deductionPoints || 0) !== 0);
    if (defectItems.length === 0) return true;
    const hasPhotoAndComment = item => (
      (item.images && item.images.length > 0)
      && (item.comment && item.comment.trim().length > 0)
    );
    return defectItems.every(item => {
      const rec = qgsStore.getRecord(item.id);
      const tri = rec?.response;
      if (!tri) return false;
      if (tri === 'OK') return true;
      if (tri === 'KO') return !!rec?.synced && hasPhotoAndComment(item);
      if (tri === 'REWORK') return hasPhotoAndComment(item);
      return false;
    });
  })();

  // Image compression utility
  const compressImage = (file, targetSizeKB = 200, maxWidth = 1920, maxHeight = 1080) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Start with quality 0.8 and reduce until under target size
        let quality = 0.8;
        let compressed;

        const compress = () => {
          compressed = canvas.toDataURL('image/jpeg', quality);
          const sizeKB = Math.round((compressed.length * 0.75) / 1024);

          if (sizeKB > targetSizeKB && quality > 0.1) {
            quality -= 0.1;
            compress();
          } else {
            // Convert base64 to File object
            fetch(compressed)
              .then(res => res.blob())
              .then(blob => {
                const compressedFile = new File([blob], file.name || 'camera-capture.jpg', {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              });
          }
        };

        compress();
      };

      img.src = typeof file === 'string' ? file : URL.createObjectURL(file);
    });
  };

  // Initialize camera
  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 }
        }
      });

      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      return true;
    } catch (error) {
      console.error('Camera initialization failed:', error);
      return false;
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Capture image from camera
  const captureImage = async () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageDataUrl);
    stopCamera();
  };

  // Handle image options dialog
  const openImageOptions = (itemId) => {
    setImageOptionsDialog({ open: true, itemId });
  };

  // Handle camera option
  const handleCameraOption = async (itemId) => {
    setImageOptionsDialog({ open: false, itemId: null });
    setCameraDialog({ open: true, itemId });

    // Small delay to ensure dialog is rendered
    setTimeout(async () => {
      await initializeCamera();
    }, 100);
  };

  // Handle upload option
  const handleUploadOption = (itemId) => {
    setImageOptionsDialog({ open: false, itemId: null });
    // Trigger file input
    document.getElementById(`image-upload-${itemId}`).click();
  };

  // Handle captured image save
  const handleSaveCapturedImage = async () => {
    if (!capturedImage || !cameraDialog.itemId) return;

    try {
      // Create a file from the captured image
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });

      // Compress the image
      const compressedFile = await compressImage(file);

      // Create a fake event object for handleImageUpload
      const fakeEvent = {
        target: {
          files: [compressedFile]
        }
      };

      // Use existing upload logic
      await handleImageUpload(cameraDialog.itemId, fakeEvent);

      // Clean up
      setCapturedImage(null);
      setCameraDialog({ open: false, itemId: null });
    } catch (error) {
      console.error('Error saving captured image:', error);
    }
  };

  // Clean up camera on dialog close
  const handleCameraDialogClose = () => {
    stopCamera();
    setCapturedImage(null);
    setCameraDialog({ open: false, itemId: null });
  };

  const handleItemChange = (updatedItem, fromUpload = false) => {
    console.log(`🔥 CHECKLIST PAGE - handleItemChange called:`, {
      itemId: updatedItem.id,
      isChecked: updatedItem.isChecked,
      fromUpload,
      hasOnItemChange: typeof onItemChange === 'function'
    });

    // Prevent state changes while upload is in progress (except when called from upload itself)
    if (uploadInProgressRef.current[updatedItem.id] && !fromUpload) {
      console.warn(`Upload in progress for item ${updatedItem.id}, deferring state change`);
      return;
    }

    // If unchecking an item, clear its images and comments
    if (!updatedItem.isChecked) {
      updatedItem = {
        ...updatedItem,
        images: [],
        comment: '' // Use 'comment' (singular)
      };
    }

    // Implement mutual exclusivity logic
    if (updatedItem.isChecked) {
      const isNoProblemsDetected = updatedItem.deductionPoints === 0;

      // If "No problems detected" is being checked, uncheck all other items
      if (isNoProblemsDetected) {
        const updatedItems = checklistItems.map(item => ({
          ...item,
          isChecked: item.id === updatedItem.id ? true : (item.deductionPoints === 0 ? item.isChecked : false),
          // Clear images and comments for unchecked items
          images: item.id === updatedItem.id ? item.images : [],
          comment: item.id === updatedItem.id ? item.comment : '' // Use 'comment' (singular)
        }));

        // Apply changes to all items
        updatedItems.forEach(item => {
          if (item.id !== updatedItem.id && item.isChecked !== (checklistItems.find(orig => orig.id === item.id)?.isChecked || false)) {
            if (onItemChange) {
              onItemChange(item);
            }
          }
        });
      } else {
        // If any other item is being checked, uncheck all "No problems detected" items
        const noProblemsItems = checklistItems.filter(item => item.deductionPoints === 0);
        noProblemsItems.forEach(noProblemItem => {
          if (noProblemItem.isChecked) {
            if (onItemChange) {
              onItemChange({ ...noProblemItem, isChecked: false, images: [], comment: '' }); // Use 'comment' (singular)
            }
          }
        });
      }
    }

    // Apply the main change
    if (onItemChange) {
      onItemChange(updatedItem);
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    }
  };

  // Enhanced image upload with Cloudinary
  const handleImageUpload = async (itemId, event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Prevent concurrent uploads for the same item
    if (uploadInProgressRef.current[itemId]) {
      console.warn(`Upload already in progress for item ${itemId}`);
      return;
    }

    // Mark upload as in progress
    uploadInProgressRef.current[itemId] = true;

    // Check if Cloudinary is configured - show gentle warning, not alert
    if (!cloudinaryService.isConfigured()) {
      console.error('Cloudinary is not configured. Images will be stored locally.');
      // Create local-only images without uploading to Cloudinary
      const item = checklistItems.find(i => i.id === itemId);
      if (!item) {
        uploadInProgressRef.current[itemId] = false;
        return;
      }

      // Compress images even for local storage
      const compressedImages = await Promise.all(
        Array.from(files).map(async (file) => {
          const compressedFile = await compressImage(file);
          return {
            id: Math.random().toString(36).substr(2, 9),
            file: compressedFile,
            url: URL.createObjectURL(compressedFile),
            alt: `Image for ${item.label}`,
            uploadStatus: 'local',
            localFile: true
          };
        })
      );

      handleItemChange({ ...item, images: [...(item.images || []), ...compressedImages] }, true); // Pass true for upload
      uploadInProgressRef.current[itemId] = false;
      return;
    }

    const item = checklistItems.find(i => i.id === itemId);
    if (!item) {
      uploadInProgressRef.current[itemId] = false;
      return;
    }

    // Set uploading state
    setUploadingItems(prev => ({ ...prev, [itemId]: true }));
    setUploadProgress(prev => ({ ...prev, [itemId]: 0 }));

    const newImages = [];
    const totalFiles = files.length;

    for (let i = 0; i < totalFiles; i++) {
      const file = files[i];

      // Validate file type
      const allowedFormats = (process.env.REACT_APP_ALLOWED_IMAGE_FORMATS || 'jpg,jpeg,png,gif,webp').split(',');
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!allowedFormats.includes(fileExtension)) {
        alert(`File ${file.name} has an invalid format. Allowed formats: ${allowedFormats.join(', ')}`);
        continue;
      }

      // Compress image before upload
      const compressedFile = await compressImage(file);
      console.log(`📸 Image compressed: ${file.name} - Original: ${Math.round(file.size/1024)}KB -> Compressed: ${Math.round(compressedFile.size/1024)}KB`);

      // Create temporary image object with local preview
      const tempImage = {
        id: Math.random().toString(36).substr(2, 9),
        file: compressedFile,
        url: URL.createObjectURL(compressedFile),
        alt: `Image for ${item.label}`,
        uploadStatus: 'uploading',
        localFile: true
      };

      newImages.push(tempImage);

      // Update progress
      setUploadProgress(prev => ({
        ...prev,
        [itemId]: Math.round(((i + 1) / totalFiles) * 100)
      }));

      // Upload to Cloudinary
      try {
        const uploadOptions = {
          folder: `${process.env.REACT_APP_CLOUDINARY_UPLOAD_FOLDER || 'audit-images'}/${selectedForm?.id || 'general'}`,
          tags: [
            `audit_${selectedForm?.id}`,
            `category_${categoryTitle}`,
            `control_${controlItemTitle}`,
            `item_${itemId}`
          ],
          context: `item=${item.label}|category=${categoryTitle}`
          // NOTE: 'eager' transformations not allowed for unsigned uploads
          // Thumbnails will be generated on-the-fly via URL transformation
        };

        const result = await cloudinaryService.uploadImage(compressedFile, uploadOptions);

        if (result.success) {
          // Update image with Cloudinary URL
          tempImage.url = result.data.url;
          tempImage.thumbnailUrl = result.data.thumbnailUrl || cloudinaryService.getThumbnailUrl(result.data.url, 200);
          tempImage.cloudinaryId = result.data.id;
          tempImage.publicId = result.data.publicId;
          tempImage.uploadStatus = 'success';
          tempImage.localFile = false;
          tempImage.cloudinaryData = result.data;

          // Clean up local object URL
          if (tempImage.url.startsWith('blob:')) {
            URL.revokeObjectURL(tempImage.url);
          }

          console.log(`✅ Image uploaded successfully: ${file.name}`);
        } else {
          tempImage.uploadStatus = 'error';
          tempImage.error = result.error;
          console.error(`❌ Failed to upload ${file.name}:`, result.error);
        }
      } catch (error) {
        tempImage.uploadStatus = 'error';
        tempImage.error = error.message;
        console.error(`❌ Error uploading ${file.name}:`, error);
      }
    }

    // Update item with new images
    const updatedImages = [...(item.images || []), ...newImages];
    handleItemChange({ ...item, images: updatedImages }, true); // Pass true to indicate this is from upload

    // Clear uploading state
    setUploadingItems(prev => ({ ...prev, [itemId]: false }));
    setUploadProgress(prev => ({ ...prev, [itemId]: 0 }));
    uploadInProgressRef.current[itemId] = false; // Clear upload in progress flag

    // Show success message
    const successCount = newImages.filter(img => img.uploadStatus === 'success').length;
    const errorCount = newImages.filter(img => img.uploadStatus === 'error').length;

    if (successCount > 0 && errorCount === 0) {
      console.log(`✅ All ${successCount} image(s) uploaded successfully`);
    } else if (successCount > 0 && errorCount > 0) {
      console.log(`⚠️ ${successCount} image(s) uploaded, ${errorCount} failed`);
    } else if (errorCount > 0) {
      console.log(`❌ Failed to upload ${errorCount} image(s)`);
    }
  };

  const openImageDialog = (item) => {
    setSelectedItem(item);
    setImageDialogOpen(true);
  };

  const openCommentDialog = (item) => {
    setSelectedItem(item);
    setTempComment(item.comment || '');
    setCommentDialogOpen(true);
  };

  const saveComment = () => {
    if (selectedItem) {
      handleItemChange({ ...selectedItem, comment: tempComment });
    }
    setCommentDialogOpen(false);
    setSelectedItem(null);
    setTempComment('');
  };

  const removeImage = (itemId, imageId) => {
    const item = checklistItems.find(i => i.id === itemId);
    if (!item) return;
    
    const imageToRemove = item.images?.find(img => img.id === imageId);
    if (imageToRemove?.localFile && imageToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.url);
    }
    
    const updatedImages = (item.images || []).filter(img => img.id !== imageId);
    handleItemChange({ ...item, images: updatedImages });
  };

  const viewFullImage = (image) => {
    setViewImageDialog({ open: true, image });
  };

  // Check Cloudinary configuration on mount - ONLY LOG, NO ALERTS
  React.useEffect(() => {
    const configured = cloudinaryService.isConfigured();
    setCloudinaryStatus(configured);

    if (!configured) {
      console.warn('⚠️ Cloudinary is not configured. Please add the following to your .env.local file:');
      console.warn('REACT_APP_CLOUDINARY_CLOUD_NAME=your_cloud_name');
      console.warn('REACT_APP_CLOUDINARY_UPLOAD_PRESET=your_upload_preset');
    } else {
      console.log('✅ Cloudinary is configured and ready');
    }
  }, []); // Empty array - runs only once

  // Cleanup camera on component unmount
  React.useEffect(() => {
    return () => {
      stopCamera();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn("pwa-flex-page bg-background", className)}>
      <div className="pwa-tablet-container container mx-auto max-w-7xl flex flex-col h-full">
        <header className="pwa-fixed-header flex-shrink-0 bg-background px-4 md:px-6 pb-4 border-b border-gray-200 opacity-0 animate-[fadeInDown_0.5s_ease-out_forwards]">
          <div className="space-y-3 mt-6">
            {/* Category Badge */}
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-600">
                {t('checklist.category')} – {language === 'it' ? categoryTranslation : categoryTitle}
              </Badge>
              {!cloudinaryStatus && (
                <Badge variant="destructive" className="animate-pulse">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {t('checklist.image_uploads_disabled')}
                </Badge>
              )}
            </div>

            {/* Navigation Buttons + Score on same row */}
            <div className="flex items-center justify-between gap-3 mt-8">
              <div className="flex items-center gap-3">
                <Button onClick={() => onNavigate('controlItemsView')} variant="outline" size="sm">
                  {t('checklist.control_items')}
                </Button>
                <Button onClick={() => onNavigate('categoryView')} variant="outline" size="sm">
                  {t('checklist.categories')}
                </Button>
                <Button onClick={() => {
                  console.log('🔥 ChecklistPage - Summary button clicked!', { onNavigate: !!onNavigate });
                  if (onNavigate) {
                    console.log('🔥 Calling onNavigate with auditSummary');
                    onNavigate('auditSummary');
                  } else {
                    console.error('❌ onNavigate is not defined!');
                  }
                }} variant="outline" size="sm">
                  {t('nav.summary')}
                </Button>
                <Button onClick={() => onNavigate('chillerAuditDashboard')} variant="outline" size="sm">
                  {t('checklist.dashboard')}
                </Button>
              </div>

              {/* QGS · KO + Rework counters for the whole audit. Prefer the
                  backend's authoritative counts (updated after every KO /
                  Rework POST); fall back to the store's local totals until
                  the first flush lands. */}
              {mockOn && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-red-50 border border-red-200 text-red-700">
                    <span className="text-lg leading-none">{backendSummary ? backendSummary.knockouts_count : qgsStore.localKoCount()}</span>
                    <span className="text-xs uppercase tracking-wider opacity-80">KO</span>
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-orange-50 border border-orange-200 text-orange-700">
                    <span className="text-lg leading-none">{backendSummary ? backendSummary.reworks_count : qgsStore.localReworkCount()}</span>
                    <span className="text-xs uppercase tracking-wider opacity-80">Rework</span>
                  </span>
                </div>
              )}
            </div>

            {/* Control Item Title — English primary, Italian sub only if present */}
            <div className="space-y-1">
              <h1 className="text-base md:text-lg lg:text-xl font-medium text-foreground leading-snug">
                {controlItemTitle}
              </h1>
              {controlItemTitleItalian && (
                <p className="text-sm md:text-base lg:text-lg text-muted-foreground italic">
                  {controlItemTitleItalian}
                </p>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 opacity-0 animate-[slideUp_0.5s_ease-out_0.2s_forwards]" style={{ paddingBottom: '140px' }}>
          <Card className="w-full shadow-lg border-0 bg-card rounded-2xl overflow-hidden">
            <CardContent className="p-0">
                <table className="w-full border-separate border-spacing-0">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="bg-muted/30">
                      {/* QGS v2 · leftmost checkbox column is dropped in QGS
                          mode — the tri-state control on each row (OK /
                          REWORK / KO) is the primary input, so the tick was
                          confusing.  Legacy QAS mode keeps its checkbox. */}
                      {!mockOn && (
                        <th className="py-4 px-6 text-left text-xs font-semibold text-muted-foreground uppercase w-8">
                          <span className="sr-only">Checkbox</span>
                        </th>
                      )}
                      <th className="py-4 px-6 text-left text-xs font-semibold text-muted-foreground uppercase min-w-0 flex-1">
                        {t('checklist.check_description')}
                      </th>
                      {/* QUANTITY + PRODUCTION PHASE columns removed for
                          the QGS operator flow — not part of the response
                          model. Only PICTURES and COMMENT are surfaced,
                          and only on Defect rows (NPD rows leave them
                          blank via the transform's is_no_problem_row flag). */}
                      <th className="py-4 px-6 text-center text-xs font-semibold text-muted-foreground uppercase w-32">
                        {t('checklist.pictures')}
                      </th>
                      <th className="py-4 px-6 text-center text-xs font-semibold text-muted-foreground uppercase w-24">
                        {t('checklist.comment')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* QGS v2 · hide the "No Problem Detected" NPD row.  With
                        every defect row carrying an OK button (variant='full')
                        the NPD row is redundant, and requiring the operator
                        to pick OK on every defect is the correct UX for this
                        gate audit. Legacy QAS mode continues to render every
                        row including NPD. */}
                    {checklistItems
                      .filter(item => !mockOn || (item.deductionPoints || 0) !== 0)
                      .map((item, index, arr) => (
                      <tr
                        key={item.id}
                        className={cn(
                          index === arr.length - 1 ? "border-b-0" : "border-b border-border hover:bg-muted/30 transition-colors",
                          "opacity-0 animate-[slideInRow_0.3s_ease-out_forwards]"
                        )}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        {!mockOn && (
                          <td className="py-4 px-6 w-8">
                            <input
                              type="checkbox"
                              checked={item.isChecked || false}
                              onChange={e => {
                                console.log(`🔥 CHECKBOX CLICKED - Item ${item.id}, checked: ${e.target.checked}`);
                                handleItemChange({ ...item, isChecked: e.target.checked });
                              }}
                              className="accent-primary h-4 w-4 rounded border-muted-foreground/20 focus:ring-2 focus:ring-primary"
                            />
                          </td>
                        )}
                        <td className="py-4 px-6 min-w-0 flex-1">
                          <div className="space-y-1">
                            {/* Show the language the operator picked as primary; when
                                the other language is populated (and different), render
                                it as an italic sub-line. MASTER-sourced QGS checks
                                often carry only English, so falling back to the
                                populated field prevents a blank Check Description. */}
                            <p className="text-sm font-medium text-foreground leading-relaxed">
                                {language === 'it'
                                  ? (item.labelItalian || item.label || '')
                                  : (item.label || item.labelItalian || '')}
                            </p>
                            {(() => {
                              const primary = language === 'it'
                                ? (item.labelItalian || item.label || '')
                                : (item.label || item.labelItalian || '');
                              const secondary = language === 'it'
                                ? (item.label || '')
                                : (item.labelItalian || '');
                              return secondary && secondary !== primary ? (
                                <p className="text-xs text-muted-foreground italic">
                                  {secondary}
                                </p>
                              ) : null;
                            })()}
                            {/* DQGMS · P-001 (mockup) — tri-state response control.
                                variant: 'npd' shows a single OK button for the No-Problem-Detected
                                row; 'defect' shows REWORK+KO only (OK is implicit — the leftmost
                                checkbox auto-ticks when a defect response is selected). */}
                            {mockOn && (
                              <div style={{ marginTop: 10, maxWidth: 480 }}>
                                <TriStateResponseControl
                                  value={triResponses[item.id] || null}
                                  onChange={(v) => handleTriChange(item, v)}
                                  details={triDetails[item.id] || {}}
                                  onDetailsChange={(patch) => handleTriDetailsChange(item.id, patch)}
                                  lookups={scopedLookupsFor(item)}
                                  gateType={selectedForm && selectedForm.gate_type ? selectedForm.gate_type : 'QG1'}
                                  language={language}
                                  variant={(item.deductionPoints || 0) === 0 ? 'npd' : 'full'}
                                />
                                {/* QGS v2 · KO approval button. Shown only for KO rows
                                    that have their two mandatory pickers filled and
                                    haven't yet been committed to the backend. Once
                                    synced, we show a small approved chip instead. */}
                                {qgsStore.getRecord(item.id)?.synced && qgsStore.getRecord(item.id)?.response === 'KO' && (
                                  <div style={{
                                    marginTop: 8, padding: '6px 10px', borderRadius: 6,
                                    background: '#e6f6ee', border: '1px solid #22a06b',
                                    color: '#0f5a3a', fontSize: 12, fontWeight: 600,
                                  }}>
                                    ✓ KO approved{qgsStore.getRecord(item.id)?.approver?.username
                                      ? ` by ${qgsStore.getRecord(item.id).approver.username}` : ''}
                                  </div>
                                )}
                                {isKoReadyForApproval(item) && (
                                  <button
                                    type="button"
                                    onClick={() => setKoApproval({ open: true, item })}
                                    style={{
                                      marginTop: 8, padding: '8px 14px', border: 'none',
                                      borderRadius: 6, background: '#d94a3a', color: '#fff',
                                      fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                    }}
                                  >
                                    {language === 'it'
                                      ? 'Richiedi approvazione Capo Linea'
                                      : 'Submit for Line Leader approval'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        {/* QUANTITY column omitted for QGS. */}
                        {(() => { /* QGS v2 · KO + REWORK both require photo+comment regardless of per-item flags. */ return null; })()}
                        <td className="py-4 px-6 w-32">
                          {(item.requiresAttachment || (mockOn && (triResponses[item.id] === 'KO' || triResponses[item.id] === 'REWORK'))) ? (
                            <div className="flex items-center justify-center gap-2">
                              <input
                                type="file"
                                id={`image-upload-${item.id}`}
                                multiple
                                accept="image/*"
                                onChange={e => handleImageUpload(item.id, e)}
                                className="hidden"
                                disabled={!cloudinaryStatus || uploadingItems[item.id]}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-10 w-10 p-0 hover:bg-primary/10"
                                onClick={() => openImageOptions(item.id)}
                                disabled={uploadingItems[item.id]}
                              >
                                {uploadingItems[item.id] ? (
                                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                ) : (
                                  <ImageIcon className="h-6 w-6 text-muted-foreground hover:text-primary transition-colors" />
                                )}
                              </Button>
                              {item.images && item.images.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-10 w-10 p-0 hover:bg-primary/10"
                                  onClick={() => openImageDialog(item)}
                                >
                                  <span className="text-sm font-bold text-primary">{item.images.length}</span>
                                </Button>
                              )}
                              {uploadProgress[item.id] > 0 && uploadProgress[item.id] < 100 && (
                                <span className="text-xs text-blue-600">{uploadProgress[item.id]}%</span>
                              )}
                            </div>
                          ) : (
                            <span aria-hidden="true" />
                          )}
                        </td>
                        <td className="py-4 px-6 w-24">
                          {(item.requiresComment || (mockOn && (triResponses[item.id] === 'KO' || triResponses[item.id] === 'REWORK'))) ? (
                            <div className="flex items-center justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-10 w-10 p-0 hover:bg-primary/10"
                                onClick={() => openCommentDialog(item)}
                              >
                                <MessageSquare className={cn(
                                  "h-6 w-6 transition-colors",
                                  item.comment ? "text-primary" : "text-muted-foreground hover:text-primary"
                                )} />
                              </Button>
                            </div>
                          ) : (
                            <span aria-hidden="true" />
                          )}
                        </td>
                        {/* PRODUCTION PHASE column omitted for QGS. */}
                      </tr>
                    ))}
                    
                    {/* Empty state */}
                    {checklistItems.length === 0 && (
                      <tr>
                        <td colSpan="6" className="py-12 text-center">
                          <div className="text-muted-foreground">
                            <p className="text-lg font-medium mb-2">{t('checklist.no_check_items')}</p>
                            <p className="text-sm">{t('checklist.no_check_items_desc')}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Fixed Footer Section with Progress */}
      <footer className="pwa-fixed-footer flex-shrink-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="container mx-auto max-w-7xl">
          <nav className="w-full px-4 md:px-6 py-4 pb-5">
            <div className="flex flex-col md:flex-row gap-3 md:gap-6 items-stretch md:items-center justify-between">
              <Button
                onClick={onPrevious}
                className="w-full md:w-auto text-lg font-semibold rounded-full px-8 py-4 md:py-3 md:px-10"
                size="lg"
                variant="secondary"
                disabled={isNavigationDisabled}
              >
                {isNavigationDisabled ? t('checklist.loading') : t('checklist.previous')}
              </Button>

              {/* Progress widget */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 min-w-[200px]">
                <div className="text-center space-y-1">
                  <div className="text-xs font-medium text-gray-600">
                    {t('checklist.category_of').replace('{0}', currentCategoryIndex).replace('{1}', totalCategories)}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {language === 'it' ? categoryTranslation : categoryTitle}
                  </div>
                  <div className="text-xs text-blue-600">
                    {t('checklist.control_item_of').replace('{0}', currentControlItemIndex).replace('{1}', totalControlItems)}
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div 
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${(currentControlItemIndex / totalControlItems) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {(() => {
                // DQGMS mockup: relax completion rule to "at least one row ticked" per control item.
                // QAS (or production) still honours the original all-required flow.
                const nextOk = mockOn ? mockNextEnabled : isChecklistComplete;
                return (
                  <Button
                    onClick={handleNext}
                    className={cn(
                      "w-full md:w-auto text-lg font-semibold rounded-full px-8 py-4 md:py-3 md:px-10",
                      !nextOk && !isNavigationDisabled
                        ? "bg-gray-400 hover:bg-gray-500 cursor-not-allowed"
                        : ""
                    )}
                    size="lg"
                    disabled={isNavigationDisabled || !nextOk}
                    title={!nextOk ? (mockOn ? "Answer every check to continue" : t('checklist.complete_tooltip')) : ""}
                  >
                    {isNavigationDisabled
                      ? t('checklist.saving')
                      : !nextOk
                        ? (mockOn ? "Answer every check" : t('checklist.complete_required'))
                        : t('checklist.next')
                    }
                  </Button>
                );
              })()}
            </div>
          </nav>
        </div>
      </footer>

      {/* Enhanced Image Gallery Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden">
          <DialogHeader className="pb-3">
            <DialogTitle className="flex items-center justify-between">
              <span>{t('checklist.images_for').replace('{0}', selectedItem?.label || '')}</span>
              {selectedItem?.images?.length > 0 && (
                <Badge variant="secondary">{t('checklist.images_count').replace('{0}', selectedItem.images.length)}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[calc(85vh-100px)]">
            {selectedItem?.images?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedItem.images.map(image => (
                  <ImagePreview
                    key={image.id}
                    image={image}
                    onRemove={(imageId) => removeImage(selectedItem.id, imageId)}
                    onView={viewFullImage}
                    t={t}
                  />
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">{t('checklist.no_images')}</p>
                <p className="text-sm text-gray-400">{t('checklist.use_camera')}</p>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-3 border-t">
            <div className="text-sm text-gray-500">
              {cloudinaryStatus ? (
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t('checklist.cloud_storage')}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  {t('checklist.cloud_not_configured')}
                </span>
              )}
            </div>
            <label htmlFor={`gallery-upload-${selectedItem?.id}`}>
              <input
                type="file"
                id={`gallery-upload-${selectedItem?.id}`}
                multiple
                accept="image/*"
                onChange={e => selectedItem && handleImageUpload(selectedItem.id, e)}
                className="hidden"
                disabled={!cloudinaryStatus}
              />
              <Button variant="outline" size="sm" asChild disabled={!cloudinaryStatus}>
                <span className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  {t('checklist.add_more_images')}
                </span>
              </Button>
            </label>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Image View Dialog */}
      <Dialog open={viewImageDialog.open} onOpenChange={(open) => setViewImageDialog({ open, image: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          {viewImageDialog.image && (
            <div className="relative">
              <img 
                src={viewImageDialog.image.url} 
                alt={viewImageDialog.image.alt} 
                className="w-full h-auto max-h-[90vh] object-contain"
              />
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
                onClick={() => setViewImageDialog({ open: false, image: null })}
              >
                <X className="h-4 w-4" />
              </Button>
              {viewImageDialog.image.cloudinaryData && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-3 text-xs">
                  <div className="flex justify-between items-center">
                    <span>{viewImageDialog.image.cloudinaryData.originalFilename}</span>
                    <span>{Math.round(viewImageDialog.image.cloudinaryData.bytes / 1024)} KB</span>
                    <span>{viewImageDialog.image.cloudinaryData.width} × {viewImageDialog.image.cloudinaryData.height}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('checklist.comment_for').replace('{0}', selectedItem?.label || '')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Textarea
              value={tempComment}
              onChange={e => setTempComment(e.target.value)}
              placeholder={t('checklist.enter_comment')}
              className="min-h-[100px] resize-none"
            />
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setTempComment('')} className="text-red-600 hover:text-red-700">
                {t('checklist.clear')}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setCommentDialogOpen(false); setTempComment(''); }}>
                  {t('checklist.cancel')}
                </Button>
                <Button onClick={saveComment}>{t('checklist.save_comment')}</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Options Dialog */}
      <Dialog open={imageOptionsDialog.open} onOpenChange={(open) => setImageOptionsDialog({ open, itemId: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('checklist.add_image')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">{t('checklist.choose_method')}</p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="h-24 flex flex-col gap-2"
                onClick={() => handleCameraOption(imageOptionsDialog.itemId)}
              >
                <Camera className="h-8 w-8" />
                <span>{t('checklist.take_photo')}</span>
              </Button>
              <Button
                variant="outline"
                className="h-24 flex flex-col gap-2"
                onClick={() => handleUploadOption(imageOptionsDialog.itemId)}
              >
                <Upload className="h-8 w-8" />
                <span>{t('checklist.upload_file')}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera Capture Dialog */}
      <Dialog open={cameraDialog.open} onOpenChange={handleCameraDialogClose}>
        <DialogContent className="w-[min(70vw,800px)] h-[min(70vh,600px)] max-w-[800px] max-h-[600px] p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>{t('checklist.take_photo')}</DialogTitle>
          </DialogHeader>
          <div className="p-4 h-full flex flex-col">
            {!capturedImage ? (
              <div className="flex-1 flex flex-col space-y-4">
                <div className="relative bg-black rounded-lg overflow-hidden flex-1 min-h-0">
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
                    <Button
                      onClick={captureImage}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-20 h-20 shadow-2xl border-4 border-white/20 transition-all duration-200 hover:scale-105"
                    >
                      <Camera className="h-8 w-8" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-center text-gray-600 py-2">
                  {t('checklist.position_camera')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative rounded-lg overflow-hidden">
                  <img
                    src={capturedImage}
                    alt="Captured"
                    className="w-full h-auto max-h-96 object-contain"
                  />
                </div>
                <div className="flex justify-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCapturedImage(null);
                      initializeCamera();
                    }}
                  >
                    {t('checklist.retake')}
                  </Button>
                  <Button onClick={handleSaveCapturedImage}>
                    {t('checklist.save_image')}
                  </Button>
                </div>
                <p className="text-sm text-center text-gray-600">
                  {t('checklist.compress_note')}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRow {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* QGS v2 · Line Leader approval dialog. Mounted at the page root so
          it overlays the checklist. Closed by default. */}
      <KoApprovalModal
        isOpen={koApproval.open}
        onClose={() => setKoApproval({ open: false, item: null })}
        onApproved={() => setKoApproval({ open: false, item: null })}
        onSubmit={submitKoApproval}
        itemLabel={koApproval.item ? (language === 'it'
          ? (koApproval.item.labelItalian || koApproval.item.label)
          : (koApproval.item.label || koApproval.item.labelItalian)) : ''}
        language={language}
      />
    </div>
  );
}

export default ChecklistPage;